import React, { useState, useEffect, useMemo } from 'react';
import './RecipientTable.css';

const RecipientTable = ({ recipients, onSelectionChange, loading }) => {
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [filter, setFilter] = useState('all'); // 'all', 'withEmail', 'cpf', 'cnpj', 'oab'
  const [searchTerm, setSearchTerm] = useState('');
  const [groupByOrcamento, setGroupByOrcamento] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState(new Set()); // Track which year groups are expanded
  
  // Flatten recipients into a single array with type info
  const allRecipients = useMemo(() => [
    ...recipients.cpf.map(r => ({ ...r, displayType: 'CPF' })),
    ...recipients.cnpj.map(r => ({ ...r, displayType: 'CNPJ' })),
    ...recipients.oab.map(r => ({ ...r, displayType: 'OAB' }))
  ], [recipients]);
  
  // Apply filters
  const filteredRecipients = useMemo(() => {
    return allRecipients.filter(recipient => {
      // Type filter
      if (filter === 'cpf' && recipient.type !== 'cpf') return false;
      if (filter === 'cnpj' && recipient.type !== 'cnpj') return false;
      if (filter === 'oab' && recipient.type !== 'oab') return false;
      if (filter === 'withEmail' && !recipient.hasEmail) return false;
      
      // Search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        return (
          recipient.identifier.toLowerCase().includes(search) ||
          recipient.nome.toLowerCase().includes(search) ||
          (recipient.email && recipient.email.toLowerCase().includes(search))
        );
      }
      
      return true;
    });
  }, [allRecipients, filter, searchTerm]);
  
  // Group recipients by ano_pagamento if enabled
  const groupedData = useMemo(() => {
    if (!groupByOrcamento) {
      return null;
    }
    
    const groups = new Map();
    filteredRecipients.forEach(recipient => {
      const ano_pagamento = recipient.ano_pagamento || 9999;
      if (!groups.has(ano_pagamento)) {
        groups.set(ano_pagamento, []);
      }
      groups.get(ano_pagamento).push(recipient);
    });
    
    // Convert to sorted array
    return Array.from(groups.entries())
      .sort(([a], [b]) => a - b)
      .map(([ano_pagamento, recipients]) => ({
        ano_pagamento,
        recipients,
        totalValue: recipients.reduce((sum, r) => sum + (r.total || 0), 0),
        count: recipients.length
      }));
  }, [filteredRecipients, groupByOrcamento]);
  
  useEffect(() => {
    // Notify parent of selection changes
    const selected = filteredRecipients.filter(r => 
      selectedItems.has(`${r.type}_${r.identifier}`) && r.hasEmail
    );
    onSelectionChange(selected);
  }, [selectedItems, filteredRecipients, onSelectionChange]);
  
  const handleSelectAll = (checked) => {
    if (checked) {
      const newSelected = new Set();
      filteredRecipients.forEach(r => {
        if (r.hasEmail) {
          newSelected.add(`${r.type}_${r.identifier}`);
        }
      });
      setSelectedItems(newSelected);
    } else {
      setSelectedItems(new Set());
    }
  };
  
  const handleSelectItem = (recipient) => {
    if (!recipient.hasEmail) return;
    
    const key = `${recipient.type}_${recipient.identifier}`;
    const newSelected = new Set(selectedItems);
    
    if (newSelected.has(key)) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }
    
    setSelectedItems(newSelected);
  };
  
  const toggleGroupExpansion = (ano_pagamento) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(ano_pagamento)) {
      newExpanded.delete(ano_pagamento);
    } else {
      newExpanded.add(ano_pagamento);
    }
    setExpandedGroups(newExpanded);
  };
  
  const expandAllGroups = () => {
    if (groupedData) {
      const allYears = new Set(groupedData.map(g => g.ano_pagamento));
      setExpandedGroups(allYears);
    }
  };
  
  const collapseAllGroups = () => {
    setExpandedGroups(new Set());
  };
  
  const formatIdentifier = (type, identifier) => {
    if (type === 'cpf' && identifier.length === 11) {
      return identifier.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    if (type === 'cnpj' && identifier.length === 14) {
      return identifier.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return identifier;
  };
  
  const recipientsWithEmail = filteredRecipients.filter(r => r.hasEmail);
  const allChecked = recipientsWithEmail.length > 0 && 
    recipientsWithEmail.every(r => selectedItems.has(`${r.type}_${r.identifier}`));
  
  if (loading) {
    return (
      <div className="recipient-table-loading">
        <div className="spinner"></div>
        <p>Carregando destinatários...</p>
      </div>
    );
  }
  
  return (
    <div className="recipient-table-container">
      <div className="table-header">
        <h3>Selecione os Destinatários</h3>
        <div className="table-controls">
          <input
            type="text"
            placeholder="Buscar por CPF, CNPJ, OAB, nome ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <select 
            value={filter} 
            onChange={(e) => setFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">Todos</option>
            <option value="withEmail">Apenas com Email</option>
            <option value="cpf">Apenas CPF</option>
            <option value="cnpj">Apenas CNPJ</option>
            <option value="oab">Apenas OAB</option>
          </select>
          <label className="group-toggle">
            <input
              type="checkbox"
              checked={groupByOrcamento}
              onChange={(e) => setGroupByOrcamento(e.target.checked)}
            />
            Agrupar por Ano de Pagamento
          </label>
          {groupByOrcamento && groupedData && groupedData.length > 0 && (
            <div className="expand-controls">
              <button 
                className="expand-btn"
                onClick={expandAllGroups}
                title="Expandir todos"
              >
                ▼ Expandir Todos
              </button>
              <button 
                className="collapse-btn"
                onClick={collapseAllGroups}
                title="Recolher todos"
              >
                ▲ Recolher Todos
              </button>
            </div>
          )}
        </div>
      </div>
      
      <div className="table-stats">
        <span>Exibindo {filteredRecipients.length} destinatários</span>
        <span className="separator">|</span>
        <span>{recipientsWithEmail.length} com email</span>
        <span className="separator">|</span>
        <span className="selected-count">{selectedItems.size} selecionados</span>
      </div>
      
      <div className="table-wrapper">
        {groupByOrcamento && groupedData ? (
          // Grouped view by ano_pagamento
          <div className="grouped-view">
            {groupedData.length === 0 ? (
              <div className="no-data">Nenhum destinatário encontrado</div>
            ) : (
              groupedData.map((group) => {
                const isExpanded = expandedGroups.has(group.ano_pagamento);
                const selectedInGroup = group.recipients.filter(r => 
                  r.hasEmail && selectedItems.has(`${r.type}_${r.identifier}`)
                ).length;
                
                return (
                  <div key={group.ano_pagamento} className="orcamento-group">
                    <div 
                      className="group-header clickable"
                      onClick={() => toggleGroupExpansion(group.ano_pagamento)}
                    >
                      <div className="header-left">
                        <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
                          ▶
                        </span>
                        <h3>Ano Pagamento {group.ano_pagamento === 9999 ? 'Não Definido' : group.ano_pagamento}</h3>
                      </div>
                      <div className="group-stats">
                        {selectedInGroup > 0 && (
                          <>
                            <span className="selected-badge">{selectedInGroup} selecionado(s)</span>
                            <span className="separator">|</span>
                          </>
                        )}
                        <span>{group.count} destinatário(s)</span>
                        <span className="separator">|</span>
                        <span>Valor Total: R$ {group.totalValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                    {isExpanded && (
                      <table className="recipient-table">
                    <thead>
                      <tr>
                        <th className="checkbox-column">
                          <input
                            type="checkbox"
                            checked={group.recipients.filter(r => r.hasEmail).every(r => 
                              selectedItems.has(`${r.type}_${r.identifier}`)
                            )}
                            onChange={(e) => {
                              const newSelected = new Set(selectedItems);
                              group.recipients.forEach(r => {
                                if (r.hasEmail) {
                                  const key = `${r.type}_${r.identifier}`;
                                  if (e.target.checked) {
                                    newSelected.add(key);
                                  } else {
                                    newSelected.delete(key);
                                  }
                                }
                              });
                              setSelectedItems(newSelected);
                            }}
                            disabled={group.recipients.filter(r => r.hasEmail).length === 0}
                          />
                        </th>
                        <th>Tipo</th>
                        <th>Identificador</th>
                        <th>Nome</th>
                        <th>Email</th>
                        <th>Valor Total</th>
                        <th>Qtd. Precatórios</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.recipients.map((recipient) => {
                        const key = `${recipient.type}_${recipient.identifier}`;
                        const isSelected = selectedItems.has(key);
                        
                        return (
                          <tr 
                            key={key}
                            className={`${!recipient.hasEmail ? 'no-email' : ''} ${isSelected ? 'selected' : ''}`}
                          >
                            <td className="checkbox-column">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleSelectItem(recipient)}
                                disabled={!recipient.hasEmail}
                              />
                            </td>
                            <td>
                              <span className={`type-badge ${recipient.type}`}>
                                {recipient.displayType}
                              </span>
                            </td>
                            <td className="identifier">
                              {formatIdentifier(recipient.type, recipient.identifier)}
                            </td>
                            <td>{recipient.nome}</td>
                            <td>
                              {recipient.email || (
                                <span className="no-email-text">Email não disponível</span>
                              )}
                            </td>
                            <td>R$ {(recipient.total || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                            <td>
                              <span className="precatorio-count">
                                {recipient.precatorios.length}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                    )}
                  </div>
                );
              })
            )}
          </div>
        ) : (
          // Regular flat view
          <table className="recipient-table">
            <thead>
              <tr>
                <th className="checkbox-column">
                  <input
                    type="checkbox"
                    checked={allChecked}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    disabled={recipientsWithEmail.length === 0}
                  />
                </th>
                <th>Tipo</th>
                <th>Identificador</th>
                <th>Nome</th>
                <th>Email</th>
                <th>Ano Pagamento</th>
                <th>Valor Total</th>
                <th>Precatórios</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecipients.length === 0 ? (
                <tr>
                  <td colSpan="8" className="no-data">
                    Nenhum destinatário encontrado
                  </td>
                </tr>
              ) : (
                filteredRecipients.map((recipient, index) => {
                  const key = `${recipient.type}_${recipient.identifier}`;
                  const isSelected = selectedItems.has(key);
                  
                  return (
                    <tr 
                      key={key}
                      className={`${!recipient.hasEmail ? 'no-email' : ''} ${isSelected ? 'selected' : ''}`}
                    >
                      <td className="checkbox-column">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSelectItem(recipient)}
                          disabled={!recipient.hasEmail}
                        />
                      </td>
                      <td>
                        <span className={`type-badge ${recipient.type}`}>
                          {recipient.displayType}
                        </span>
                      </td>
                      <td className="identifier">
                        {formatIdentifier(recipient.type, recipient.identifier)}
                      </td>
                      <td>{recipient.nome}</td>
                      <td>
                        {recipient.email || (
                          <span className="no-email-text">Email não disponível</span>
                        )}
                      </td>
                      <td>{recipient.ano_pagamento === 9999 ? '-' : recipient.ano_pagamento}</td>
                      <td>R$ {(recipient.total || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                      <td>
                        <span className="precatorio-count">
                          {recipient.precatorios.length} precatório(s)
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default RecipientTable;